// client/src/components/auth/Unauthorized.js
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ShieldX, ArrowLeft, Mail } from 'lucide-react';
import { animate, stagger } from 'animejs';
import {
  AuthBackground,
  generateParticles,
  runBackgroundAnimations,
  runCardEntrance,
} from './Login';
import './Login.css';

const Unauthorized = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const cardRef = useRef(null);
  const mazeRef = useRef(null);
  const particlesRef = useRef(null);
  const animationsRan = useRef(false);
  const particles = useMemo(() => generateParticles(20), []);

  const runAnimations = useCallback(() => {
    if (animationsRan.current) return;
    animationsRan.current = true;

    runBackgroundAnimations(mazeRef, particlesRef);
    runCardEntrance(cardRef);

    // Denied icon shake — a subtle attention-grabber after card lands
    const iconEl = cardRef.current?.querySelector('.auth-denied-icon');
    if (iconEl) {
      setTimeout(() => {
        try {
          animate(iconEl, {
            rotate: [0, -6, 6, -4, 4, -2, 0],
            duration: 600,
            ease: 'inOutSine',
          });
        } catch (e) {
          /* animation optional */
        }
      }, 1200);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(runAnimations, 80);
    return () => clearTimeout(timer);
  }, [runAnimations]);

  return (
    <div className="login-page">
      <AuthBackground particles={particles} mazeRef={mazeRef} particlesRef={particlesRef} />

      <div className="login-card login-card--alert" ref={cardRef}>
        <div className="login-animate auth-denied-code">Error 403</div>

        <div className="login-animate auth-denied-icon">
          <ShieldX size={28} />
        </div>

        <h2 className="login-animate auth-denied-title">Access Denied</h2>
        <p className="login-animate auth-denied-text">
          You don&apos;t have permission to access this resource.
        </p>
        <p className="login-animate auth-denied-hint">
          If you believe this is an error, contact your system administrator to request the required
          role assignment.
        </p>

        <div className="login-animate auth-denied-divider" />

        <div className="login-animate auth-denied-actions">
          <button onClick={() => navigate('/')} className="login-button">
            <ArrowLeft size={18} />
            Return to Dashboard
          </button>

          <button
            onClick={() => {
              if (logout) {
                logout();
              } else {
                navigate('/login');
              }
            }}
            className="login-button login-button--ghost"
          >
            <Mail size={16} />
            Sign in as different user
          </button>
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
